
import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';

interface University {
  id: string;
  name_en: string;
  name_ar: string;
  city: string;
}

interface UniversitySearchBoxProps {
  value?: string;
  onValueChange: (value: string) => void;
  error?: string;
}

const UniversitySearchBox = ({ value, onValueChange, error }: UniversitySearchBoxProps) => {
  const { t, language } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [universities, setUniversities] = useState<University[]>([]);
  const [filteredUniversities, setFilteredUniversities] = useState<University[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchUniversities = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('universities')
          .select('id, name_en, name_ar, city')
          .order('name_en');

        if (error) {
          console.error('Error fetching universities:', error);
        } else {
          setUniversities(data || []);
        }
      } catch (error) {
        console.error('Error fetching universities:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUniversities();
  }, []);

  // Update search term when value changes
  useEffect(() => {
    if (value && universities.length > 0) {
      const university = universities.find(u => u.id === value);
      if (university) {
        setSearchTerm(language === 'ar' ? university.name_ar : university.name_en);
      }
    } else if (!value) {
      setSearchTerm('');
    }
  }, [value, universities, language]);

  // Filter universities based on search term
  useEffect(() => {
    if (searchTerm.trim() && universities.length > 0) {
      const filtered = universities.filter(university => {
        const nameToSearch = language === 'ar' ? university.name_ar : university.name_en;
        const cityToSearch = university.city || '';
        
        return nameToSearch.toLowerCase().includes(searchTerm.toLowerCase()) ||
               cityToSearch.toLowerCase().includes(searchTerm.toLowerCase());
      });
      setFilteredUniversities(filtered);
      setShowDropdown(true);
    } else if (searchTerm.length === 0 && universities.length > 0) {
      // Show top 10 universities when field is empty but focused
      setFilteredUniversities(universities.slice(0, 10));
    } else {
      setFilteredUniversities([]);
      setShowDropdown(false);
    }
  }, [searchTerm, universities, language]);

  const handleUniversitySelect = (university: University) => {
    const displayName = language === 'ar' ? university.name_ar : university.name_en;
    setSearchTerm(displayName);
    setShowDropdown(false);
    onValueChange(university.id);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    
    if (!newValue.trim()) {
      onValueChange('');
      setShowDropdown(false);
    }
  };

  const handleInputFocus = () => {
    if (universities.length > 0) {
      if (searchTerm.trim()) {
        setShowDropdown(true);
      } else {
        // Show top 10 universities when empty
        setFilteredUniversities(universities.slice(0, 10));
        setShowDropdown(true);
      }
    }
  };

  const handleInputBlur = () => {
    // Delay hiding dropdown to allow for clicks
    setTimeout(() => {
      setShowDropdown(false);
    }, 200);
  };

  return (
    <div>
      <div className="relative">
        <Input
          type="text"
          placeholder={loading ? t('loading', 'جاري التحميل...', 'Loading...') : t('search_university', 'ابحث عن جامعتك...', 'Search for your university...')}
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          disabled={loading}
          className={`rounded-2xl border-2 border-teal-500 focus:border-teal-600 focus-visible:ring-0 focus-visible:ring-offset-0 ${error ? 'border-destructive' : ''}`}
        />
        
        {showDropdown && filteredUniversities.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-1 z-[9999] max-h-60 overflow-y-auto bg-background/95 backdrop-blur-sm border border-border rounded-lg shadow-lg">
            {filteredUniversities.map((university) => (
              <div
                key={university.id}
                className="p-4 hover:bg-accent/50 cursor-pointer border-b border-border/30 last:border-b-0 transition-colors"
                onClick={() => handleUniversitySelect(university)}
              >
                <div className="font-semibold text-foreground">
                  {language === 'ar' ? university.name_ar : university.name_en}
                </div>
                <div className="text-sm text-muted-foreground mt-1">{university.city}</div>
              </div>
            ))}
          </div>
        )}

        {showDropdown && filteredUniversities.length === 0 && searchTerm.trim() && !loading && (
          <div className="absolute top-full left-0 right-0 mt-1 z-[9999] p-4 bg-background/95 backdrop-blur-sm border border-border rounded-lg shadow-lg">
            <div className="text-center text-muted-foreground">
              {t('no_universities_found', 'لا توجد جامعات', 'No universities found')}
            </div>
          </div>
        )}

        {universities.length === 0 && !loading && (
          <div className="absolute top-full left-0 right-0 mt-1 z-[9999] p-4 bg-background/95 backdrop-blur-sm border border-border rounded-lg shadow-lg">
            <div className="text-center text-muted-foreground text-sm">
              {t('no_universities_available', 'لا توجد جامعات متاحة في النظام', 'No universities available in the system')}
            </div>
          </div>
        )}
      </div>
      {error && (
        <p className="text-sm text-destructive mt-1">{error}</p>
      )}
    </div>
  );
};

export default UniversitySearchBox;
